import { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { Divider, FormInstance, notification, Tooltip } from 'antd';
import { Form, Input, Select, DatePicker, Button } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

import { txt } from '../../../../locales/i18n';
import { SearchForm } from '../../../shared/search-form';
import { queryMatchState } from '../../../../recoil/atoms';
import { useFormFiller } from '../../../../hooks/form-filer';

import * as EventService from '../../../../services/bb-api/event';
import type { Event, EventQuery } from '../../../../services/bb-api/event';

export type EventOverviewHeaderProps = {
	setDataSource: (dataSource: (Event & { eventId: number })[]) => void;
	setLoading: (loading: boolean) => void;
};

const initialValues = {
	amount: 200,
	status: 'active',
};

export const EventOverviewHeader = (props: EventOverviewHeaderProps) => {
	const history = useHistory();
	const setQueryMatch = useSetRecoilState(queryMatchState);

	const formRef = useRef<FormInstance>(null);
	const setQueryParams = useFormFiller(formRef);

	const handleSearchEvent = async (query: EventQuery) => {
		props.setLoading(true);
		setQueryParams(query);

		try {
			const searchResult = await EventService.search(query);
			props.setDataSource(searchResult.events);
			setQueryMatch(searchResult.totalMatches);
		} catch (error) {
			props.setDataSource([]);
			setQueryMatch(-1);

			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		props.setLoading(false);
	};

	return (
		<SearchForm ref={formRef} initialValues={initialValues} onFinish={handleSearchEvent}>
			<Form.Item>
				<Button type="ghost" onClick={() => history.push('/events/create')}>
					{txt('global.create')} <PlusOutlined />
				</Button>
			</Form.Item>

			<Divider type="vertical" />

			<Tooltip title={txt('global.event id')}>
				<Form.Item name="eventId">
					<Input type="number" placeholder={txt('global.event id')} />
				</Form.Item>
			</Tooltip>

			<Tooltip title="Status">
				<Form.Item name="status">
					<Select placeholder="Status" dropdownMatchSelectWidth={false}>
						<Select.Option value="all">{txt('global.all')}</Select.Option>
						<Select.Option value="active">{txt('global.active')}</Select.Option>
						<Select.Option value="inactive">{txt('global.inactive')}</Select.Option>
					</Select>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.date')}>
				<Form.Item name="date">
					<DatePicker.RangePicker allowEmpty={[true, true]} />
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.amount')}>
				<Form.Item name="amount">
					<Select>
						<Select.Option value={50}>50</Select.Option>
						<Select.Option value={200}>200</Select.Option>
						<Select.Option value={500}>500</Select.Option>
						<Select.Option value={1000}>1000</Select.Option>
					</Select>
				</Form.Item>
			</Tooltip>

			<Form.Item>
				<Button type="primary" htmlType="submit">
					{txt('global.search')} <SearchOutlined />
				</Button>
			</Form.Item>
		</SearchForm>
	);
};
